import { useTranslation } from 'react-i18next';
import classes from './JourneyCanvasListItem.module.scss';
import { ListItemProps } from './TypeDefs';

/**
 * JourneyCanvasListItem component.
 * Renders a list item for a journey canvas.
 *
 * @component
 * @param {ListItemProps} props - The props for the component.
 * @param {string} props.name - The name of the canvas.
 * @param {string} props.description - The description of the canvas.
 * @param {number} props.version - The version of the canvas.
 * @param {string} props.icon - The URL of the icon image for the canvas.
 * @param {string} props.dragIcon - The URL of the drag icon image.
 * @param {function} props.onDragStart - The event handler for the drag start event.
 * @param {function} props.onClick - The event handler for the click event.
 * @param {string} props.className - Additional CSS classes for the component.
 * @param {boolean} props.disable - A boolean value indicating whether the component is disabled.
 * @param {object} props.style - Additional inline styles for the component.
 * @returns {JSX.Element} The rendered JourneyCanvasListItem component.
 */
const JourneyCanvasListItem: React.FC<ListItemProps> = ({ name, description, version, icon, dragIcon, onDragStart = undefined, onClick = undefined, className = '', disable = false, style = undefined, eventName, eventVersion }: ListItemProps) => {
  const { t: translate } = useTranslation();

  const finalName = `${name} (${translate('journey_builder_component.txt_version', { version })})`;
  const finalEvent = `${eventName} (${translate('journey_builder_component.txt_version', { version: eventVersion })})`;

  return (
    <button
      type="button"
      draggable
      onDragStart={onDragStart}
      className={`${classes.item} ${className}`}
      disabled={disable}
      style={style}
      onClick={onClick}
      data-testid="journeyCanvasListItem"
    >
      <img className={classes['drag-icon']} src={dragIcon || undefined} alt="Icon" />
      <img className={classes['canvas-icon']} src={icon || undefined} alt="Icon" />
      <div className={classes.data}>
        <p className={classes.title} data-testid="canvasName" title={finalName}>
          <span className={classes.name}>{name}&nbsp;</span>
          <span className={classes.ver}>({translate('journey_builder_component.txt_version', { version })})</span>
        </p>
        <p data-testid="canvasDesc" title={description}>{description}</p>
        {eventName && <p data-testid="event" title={finalEvent}>{finalEvent}</p>}
      </div>
    </button>
  );
};

export default JourneyCanvasListItem;
